import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormGroup,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FeedbackStepData } from '../../feedback-step.interface';
import { FeedbackQuestion } from '../../../feedback';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-intervention-support-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzRadioModule,
    NzButtonModule,
    NzFormModule,
    NzDividerModule,
    NzInputModule,
  ],
  templateUrl: './intervention-support-step.component.html',
  styleUrls: ['../../feedback-form.component.scss'],
})
export class InterventionSupportStepComponent implements OnChanges, OnDestroy {
  @Input() stepData!: FeedbackStepData;
  @Input() formGroup!: FormGroup;
  @Input() onNext!: () => void;
  @Input() onPrevious!: () => void;
  @Input() overAllScore = 0;

  private destroy$ = new Subject<void>();

  ngOnChanges() {
    this.setupValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupValidation() {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;

    this.stepData.questions.forEach((question: FeedbackQuestion) => {
      const index = this.getQuestionIndex(question.id);
      if (index >= 0) {
        const control = questionResponses.at(index);
        const ratingScaleControl = control.get('ratingScale');
        const descriptionControl = control.get('description');

        if (ratingScaleControl) {
          ratingScaleControl.setValidators([Validators.required]);
          ratingScaleControl.updateValueAndValidity();
        }

        if (descriptionControl) {
          descriptionControl.setValidators(
            this.conditionalDescriptionValidator(control)
          );
          descriptionControl.updateValueAndValidity();
        }
        ratingScaleControl?.markAllAsTouched();
        descriptionControl?.markAllAsTouched();
      }
    });
  }

  conditionalDescriptionValidator(formControl: AbstractControl) {
    return (control: AbstractControl): ValidationErrors | null => {
      const ratingScale = formControl.get('ratingScale')?.value;
      const description = control.value;

      if (ratingScale === 3 && (!description || description.trim() === '')) {
        return { required: true, conditionalRequired: true };
      }

      return null;
    };
  }

  getQuestionIndex(questionId: number): number {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;
    return questionResponses.controls.findIndex(
      control => control.get('caseFeedbackQuestionId')?.value === questionId
    );
  }

  shouldShowDescription(questionId: number): boolean {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const ratingScale = control.get('ratingScale')?.value;

      return ratingScale === 3;
    }
    return false;
  }

  shouldShowDescriptionInViewMode(questionId: number): boolean {
    if (!this.stepData.isViewMode) {
      return false;
    }

    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const ratingScale = control.get('ratingScale')?.value;
      const description = control.get('description')?.value;

      return ratingScale === 3 && description && description.trim() !== '';
    }
    return false;
  }

  getRatingScaleValidationStatus(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const ratingScale = control.get('ratingScale');

      if (
        ratingScale?.invalid &&
        (ratingScale?.dirty || ratingScale?.touched)
      ) {
        return 'error';
      }
    }
    return '';
  }

  getRatingScaleErrorTip(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const ratingScale = control.get('ratingScale');

      if (
        ratingScale?.invalid &&
        (ratingScale?.dirty || ratingScale?.touched)
      ) {
        if (ratingScale.errors?.['required']) {
          return 'Answer is required';
        }
      }
    }
    return '';
  }

  getDescriptionValidationStatus(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const description = control.get('description');

      if (
        description?.invalid &&
        (description?.dirty || description?.touched)
      ) {
        return 'error';
      }
    }
    return '';
  }

  onAnswerChange(questionId: number, value: string | number | boolean): void {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const descriptionControl = control.get('description');

      if (value === 5) {
        descriptionControl?.setValue('');
      }

      if (descriptionControl) {
        descriptionControl.setValidators(
          this.conditionalDescriptionValidator(control)
        );
        descriptionControl.updateValueAndValidity();
      }
      descriptionControl?.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    }
  }
}
